import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  activatePartnerBrief,
  approvePartnerBrief,
  deactivatePartnerBrief,
  reactivatePartnerBrief,
  rejectPartnerBrief,
  sendReviewEmail,
  type BriefDetail,
  type PartnerBriefStatus,
} from '../../lib/partner-briefs-api';
import { zoneByCode } from '../../lib/zones';
import { useStaffMe } from '../../hooks/useStaffMe';
import { ActionConfirmModal } from './ActionConfirmModal';
import { DeleteBriefModal } from './DeleteBriefModal';
import { EmailComposer } from './EmailComposer';
import { Tooltip } from '../../components/Tooltip';
import { toastFromError, useToast } from '../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../lib/reauth';

/**
 * Staff action zone — the locked Decision 10-A action set on the Brief
 * detail view (Phase 5d-1, Decision 5-28).
 *
 *   Approve / Reject / Review     — always visible
 *   Activate (skip 30-day backlink) — state-aware: only when Brief is in
 *                                     pending_backlink_verification
 *
 * Each action opens an ActionConfirmModal with a non-dismissible backdrop
 * (Manage Account Ref v5 §6.5). Reject + Review use EmailComposer for the
 * editable subject/body with a server-mirrored locked footer preview.
 *
 * Server-side fresh-auth enforcement (ADR-012) returns 401 stale_auth when
 * the JWT is past its freshness window. The portal surfaces a "session
 * expired" prompt in that case so the user re-signs in; a modal-side TOTP
 * re-prompt is queued for 5d-2 alongside the anti-abuse cluster.
 *
 * "Mark Human Review" is intentionally NOT a staff action per Decision
 * 5-28 + ADR-027 §11 (human_review_required is set by the agent on
 * specific edge cases, not by staff). The admin-api mark-human-review
 * route still exists for operational override but is not surfaced here.
 *
 * On successful action the parent BriefDetailPage refreshes the Brief so
 * the new status renders and conflicting actions (e.g. Activate on a
 * now-active Brief) disappear from the zone.
 */
type Action = 'approve' | 'reject' | 'review' | 'activate' | 'deactivate' | 'reactivate';

interface DraftState {
  reviewNotes: string;
  reviewSubject: string;
  reviewBody: string;
  returnByDate: string;
  deactivationReason: string;
  reactivationNotes: string;
}

const EMPTY_DRAFT: DraftState = {
  reviewNotes: '',
  reviewSubject: 'Update needed on your Nexanomics partner application',
  reviewBody: '',
  returnByDate: '',
  deactivationReason: '',
  reactivationNotes: '',
};

export interface StaffActionZoneProps {
  brief: BriefDetail;
  onBriefUpdated: () => void;
}

export function StaffActionZone({ brief, onBriefUpdated }: StaffActionZoneProps) {
  const navigate = useNavigate();
  const me = useStaffMe();
  const toast = useToast();
  const reauth = useReAuth();
  const [active, setActive] = useState<Action | null>(null);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Delete modal is a super_admin-only action that used to live in a
  // separate Danger Zone section. Folded into Staff Actions 30 May 2026
  // per operator UAT — one action surface, less visual clutter.
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isDecisionState = isDecisionable(brief.status);
  const isActivatable =
    brief.status === 'pending_backlink_verification' || brief.status === 'approved';
  const isDeactivatable = brief.status === 'active';
  // UAT fix: brief soft-delete is operational (accounts:soft_delete = {nexa_admin, super_admin}).
  const canDeleteBrief = me.status === 'loaded' && (me.me?.role === 'super_admin' || me.me?.role === 'nexa_admin');
  const isDeletable = canDeleteBrief && brief.status !== 'deleted';
  const isReactivatable = brief.status === 'suspended';

  function openModal(a: Action) {
    setActive(a);
    setError(null);
    setDraft({
      ...EMPTY_DRAFT,
      reviewSubject:
        a === 'review'
          ? `Update needed on your Nexanomics partner application — ${brief.businessName}`
          : EMPTY_DRAFT.reviewSubject,
    });
  }

  function closeModal() {
    if (loading) return;
    setActive(null);
    setError(null);
  }

  async function dispatch() {
    if (!active) return;

    // Validation runs BEFORE the re-auth gate so a missing-field error never
    // pops the MFA modal. Each guard returns early without dispatching.
    if (active === 'reject' && !draft.reviewNotes.trim()) {
      setError('Reviewer notes are required so the applicant understands why.');
      return;
    }
    if (active === 'review' && (!draft.reviewSubject.trim() || !draft.reviewBody.trim())) {
      setError('Subject and body are both required.');
      return;
    }
    if (active === 'deactivate' && draft.deactivationReason.trim().length < 5) {
      setError('A reason of at least 5 characters is required.');
      return;
    }

    setLoading(true);
    setError(null);

    // Per-action success-toast copy. Defined as a map so success messages
    // stay aligned with their action labels — no drift if a button is
    // renamed without touching the toast text. (UAT 30 May 2026 iter 7 —
    // every state change now fires a toast so the operator gets explicit
    // confirmation, not just a silent panel refresh.)
    const successCopy: Record<Action, { title: string; detail?: string }> = {
      approve: { title: `Approved ${brief.businessName}`, detail: 'Brief is now Active. Approval email sent.' },
      reject: { title: `Rejected ${brief.businessName}`, detail: 'Rejection email sent.' },
      review: { title: `Request Info sent to ${brief.businessName}`, detail: 'Brief moves to Info Requested. Reply lands in nexa-admin.' },
      activate: { title: `Activated ${brief.businessName}` },
      deactivate: { title: `Deactivated ${brief.businessName}`, detail: 'Partner is now Suspended.' },
      reactivate: { title: `Reactivated ${brief.businessName}`, detail: 'Partner is back to Active.' },
    };

    try {
      // ADR-063: hard-block these mutating actions on a stale session —
      // withReauth gates proactively and re-auths + retries on a server 401.
      // ADR-122 Pattern 2 — EVERY action below targets the brief's OWN zone, carried on the
      // record from the queue's fan-out (or from the detail page's `?zone=`). A partner
      // application exists in exactly one zone, so approving a us-east-1 brief against
      // ap-southeast-2 would 404 on a record the operator is looking at — which reads as data
      // loss, not as a misrouted request. `zoneByCode(undefined)` resolves an untagged brief
      // to the home zone, exactly where these calls went before ADR-122.
      const zone = zoneByCode(brief.zone);
      await reauth.withReauth(async () => {
        switch (active) {
          case 'approve':
            await approvePartnerBrief(brief.applicationId, draft.reviewNotes || undefined, zone);
            break;
          case 'reject':
            await rejectPartnerBrief(brief.applicationId, draft.reviewNotes.trim(), zone);
            break;
          case 'review':
            await sendReviewEmail(brief.applicationId, {
              subject: draft.reviewSubject.trim(),
              body: draft.reviewBody.trim(),
              returnByDate: draft.returnByDate || undefined,
            }, zone);
            break;
          case 'activate':
            await activatePartnerBrief(brief.applicationId, zone);
            break;
          case 'deactivate':
            await deactivatePartnerBrief(brief.applicationId, draft.deactivationReason.trim(), zone);
            break;
          case 'reactivate':
            await reactivatePartnerBrief(
              brief.applicationId,
              draft.reactivationNotes.trim() || undefined,
              zone,
            );
            break;
        }
      });
      const copy = successCopy[active];
      toast.push({ variant: 'success', title: copy.title, detail: copy.detail });
      setActive(null);
      setDraft(EMPTY_DRAFT);
      onBriefUpdated();
    } catch (err) {
      // Operator cancelled the re-auth modal — abort silently.
      if (err instanceof ReauthCancelledError) {
        setLoading(false);
        return;
      }
      const t = toastFromError(err, `Could not ${active} ${brief.businessName}`);
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
      setError(humaniseError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="portal-action-zone" aria-label="Staff actions">
      <h2 className="portal-action-zone-label">Staff actions</h2>
      <p className="portal-action-zone-meta">
        ADR-012 Mutating tier. Brief currently in <code>{brief.status}</code>.
      </p>
      <div className="portal-action-zone-buttons">
        <button
          type="button"
          className="portal-action-btn portal-action-btn-approve"
          onClick={() => openModal('approve')}
          disabled={!isDecisionState}
        >
          ✓ Approve
        </button>
        <button
          type="button"
          className="portal-action-btn portal-action-btn-reject"
          onClick={() => openModal('reject')}
          disabled={!isDecisionState}
        >
          ✕ Reject
        </button>
        <Tooltip
          title="Request info · iterate before deciding"
          variant="warning"
          aria="Request Info: sends a templated email asking the applicant for more information. Brief moves to Info requested status; reply lands in the nexa-admin mailbox. Multiple sends allowed before Approve or Reject."
          content={
            <>
              Send a templated email asking the applicant for more information.
              Brief moves to <code>info_requested</code> and stays in the queue.
              Reply lands in the <code>nexa-admin</code> mailbox. Send as many
              Request Info emails as needed before deciding.
            </>
          }
        >
          <button
            type="button"
            className="portal-action-btn portal-action-btn-review"
            onClick={() => openModal('review')}
            disabled={!isDecisionState}
          >
            📨 Request Info
          </button>
        </Tooltip>
        {isActivatable && (
          <button
            type="button"
            className="portal-action-btn portal-action-btn-activate"
            onClick={() => openModal('activate')}
            data-state-aware="true"
            title="Manually activate this legacy Brief — needed only for Briefs created before the 30 May 2026 backlink-removal. New Approves transition directly to Active."
          >
            🔗 Activate
          </button>
        )}
        {isDeactivatable && (
          <button
            type="button"
            className="portal-action-btn portal-action-btn-deactivate"
            onClick={() => openModal('deactivate')}
            data-state-aware="true"
          >
            ⏸ Deactivate partner
          </button>
        )}
        {isReactivatable && (
          <button
            type="button"
            className="portal-action-btn portal-action-btn-reactivate"
            onClick={() => openModal('reactivate')}
            data-state-aware="true"
          >
            ▶ Reactivate partner
          </button>
        )}
        {isDeletable && (
          <button
            type="button"
            className="portal-action-btn portal-action-btn-delete"
            onClick={() => setDeleteOpen(true)}
            data-state-aware="true"
            title="Delete this Brief — soft (recoverable) or hard (permanent). super_admin only."
          >
            🗑 Delete…
          </button>
        )}
      </div>

      <ActionConfirmModal
        open={active === 'approve'}
        title={`Approve ${brief.businessName}?`}
        intent="primary"
        primaryLabel="Approve and send email"
        loading={loading}
        error={error}
        onConfirm={() => void dispatch()}
        onCancel={closeModal}
      >
        <p>
          This will transition the Brief to <code>pending_backlink_verification</code> and email the
          applicant with the standard approval message. The 30-day backlink window starts now.
        </p>
        <label className="portal-email-composer-field">
          <span>Reviewer notes (internal — not sent to applicant)</span>
          <textarea
            rows={3}
            value={draft.reviewNotes}
            maxLength={2000}
            onChange={(e) => setDraft((d) => ({ ...d, reviewNotes: e.target.value }))}
            disabled={loading}
          />
        </label>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={active === 'reject'}
        title={`Reject ${brief.businessName}?`}
        intent="danger"
        primaryLabel="Reject and send email"
        loading={loading}
        error={error}
        primaryDisabled={!draft.reviewNotes.trim()}
        onConfirm={() => void dispatch()}
        onCancel={closeModal}
      >
        <p>
          The rejection email is sent immediately. The locked footer below cannot be edited; the
          reviewer notes are surfaced verbatim to the applicant.
        </p>
        <EmailComposer
          variant="reject"
          subject=""
          body={draft.reviewNotes}
          applicantName={brief.businessName}
          disabled={loading}
          onBodyChange={(body) => setDraft((d) => ({ ...d, reviewNotes: body }))}
        />
      </ActionConfirmModal>

      <ActionConfirmModal
        open={active === 'review'}
        title={`Request info from ${brief.businessName}`}
        intent="primary"
        primaryLabel="Send email"
        loading={loading}
        error={error}
        primaryDisabled={!draft.reviewSubject.trim() || !draft.reviewBody.trim()}
        onConfirm={() => void dispatch()}
        onCancel={closeModal}
      >
        <p>
          The Brief moves to <strong>Info requested</strong> while the applicant replies. You can
          send multiple Request Info emails before deciding.
        </p>
        <EmailComposer
          variant="review"
          subject={draft.reviewSubject}
          body={draft.reviewBody}
          returnByDate={draft.returnByDate}
          applicantName={brief.businessName}
          disabled={loading}
          onSubjectChange={(subject) => setDraft((d) => ({ ...d, reviewSubject: subject }))}
          onBodyChange={(body) => setDraft((d) => ({ ...d, reviewBody: body }))}
          onReturnByDateChange={(date) => setDraft((d) => ({ ...d, returnByDate: date }))}
        />
      </ActionConfirmModal>

      <ActionConfirmModal
        open={active === 'activate'}
        title={`Activate ${brief.businessName} as a partner?`}
        intent="warning"
        primaryLabel="Activate now"
        loading={loading}
        error={error}
        onConfirm={() => void dispatch()}
        onCancel={closeModal}
      >
        <p>
          This bypasses the 30-day backlink check and marks the Brief <code>active</code> immediately.
          Use only after the partner has confirmed the backlink to nexanomics.com out-of-band.
        </p>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={active === 'deactivate'}
        title={`Deactivate ${brief.businessName}?`}
        intent="danger"
        primaryLabel="Deactivate and notify partner"
        loading={loading}
        error={error}
        primaryDisabled={draft.deactivationReason.trim().length < 5}
        onConfirm={() => void dispatch()}
        onCancel={closeModal}
      >
        <p>
          Status transitions to <code>suspended</code>. A neutral-subject email is sent to the
          partner with your reason surfaced verbatim. They can reactivate later with appropriate
          notes.
        </p>
        <label className="portal-email-composer-field">
          <span>Reason (sent to partner; 5–500 characters, required)</span>
          <textarea
            rows={4}
            value={draft.deactivationReason}
            maxLength={500}
            onChange={(e) => setDraft((d) => ({ ...d, deactivationReason: e.target.value }))}
            disabled={loading}
            required
          />
        </label>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={active === 'reactivate'}
        title={`Reactivate ${brief.businessName}?`}
        intent="primary"
        primaryLabel="Reactivate and notify partner"
        loading={loading}
        error={error}
        onConfirm={() => void dispatch()}
        onCancel={closeModal}
      >
        <p>
          Status transitions back to <code>active</code>. A brief reactivation email is sent.
          Internal notes below are captured for audit but NOT included in the email.
        </p>
        <label className="portal-email-composer-field">
          <span>Internal notes (optional, audit-only)</span>
          <textarea
            rows={3}
            value={draft.reactivationNotes}
            maxLength={2000}
            onChange={(e) => setDraft((d) => ({ ...d, reactivationNotes: e.target.value }))}
            disabled={loading}
          />
        </label>
      </ActionConfirmModal>
      {deleteOpen && (
        <DeleteBriefModal
          open
          applicationId={brief.applicationId}
          businessName={brief.businessName}
          zone={brief.zone}
          onDeleted={() => {
            setDeleteOpen(false);
            navigate('/admin/partners');
          }}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </section>
  );
}

function isDecisionable(status: PartnerBriefStatus): boolean {
  // 30 May 2026: rejected_poor_fit + rejected_auto + rejected are now
  // operator-actionable too — the agent's classification is a
  // recommendation, not a final decision. The operator can override and
  // Approve a Brief the agent marked Poor Fit, or formally Reject one
  // the agent only auto-rejected. Server-side STAFF_DECISION_SOURCES
  // must mirror this set.
  return (
    status === 'staff_review' ||
    status === 'pending_response' ||
    status === 'human_review_required' ||
    status === 'rejected_poor_fit' ||
    status === 'rejected_auto' ||
    status === 'rejected'
  );
}

function humaniseError(err: unknown): string {
  if (!(err instanceof Error)) return 'Unexpected error.';
  const msg = err.message;
  if (msg.includes('401') || msg.includes('stale_auth')) {
    // ADR-063: a 401/stale_auth is normally caught by withReauth (re-auth +
    // retry) before it reaches here. If it still surfaces, the operator
    // cancelled or re-auth failed — point them at signing in again, never a
    // hard page refresh (the old "Cmd+Shift+R" guidance is removed).
    return 'Your session has expired — please sign in again to continue.';
  }
  if (msg.includes('403')) {
    return 'Your role does not permit this action.';
  }
  if (msg.includes('409')) {
    return 'This Brief was actioned by another reviewer. Refresh the page to see the latest state.';
  }
  if (msg.includes('invalid_state')) {
    return 'This Brief is no longer in a state where that action is valid. Refresh the page.';
  }
  return msg;
}
